// @ts-nocheck
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Message,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  TableColumnProps,
  Tag
} from '@arco-design/web-react';
import { IconDelete, IconPlus, IconRefresh } from '@arco-design/web-react/icon';

import './index.scss';
import { del, get, post, put } from '../requestNew';
import { ThemeNode, ThemePage, ThemeStock } from './types';

const FormItem = Form.Item;
const API_PREFIX = '/agent/admin/v1/themes';

const SOURCE_OPTIONS = [
  { label: '全部来源', value: '' },
  { label: '普通题材（all）', value: 'all' },
  { label: '精选题材（curated）', value: 'curated' }
];

function sourceLabel(value?: string) {
  if (value === 'curated') return '精选';
  if (value === 'all') return '普通';
  return '-';
}

function normalizePayload(values: any, parent?: ThemeNode | null) {
  return {
    parentId: parent?.id || undefined,
    name: values.name?.trim(),
    sourceType: parent ? undefined : values.sourceType,
    themeKind: parent ? undefined : values.themeKind?.trim() || 'theme',
    rank: parent ? undefined : values.rank,
    sortNo: values.sortNo,
    stocks: (values.stocks || []).map((item: ThemeStock) => ({
      code: item?.code?.trim(),
      name: item?.name?.trim() || undefined,
      exchange: item?.exchange?.trim() || undefined,
      coreLogic: item?.coreLogic?.trim() || undefined
    }))
  };
}

function AgentThemeManagement() {
  const [queryForm] = Form.useForm();
  const [saveForm] = Form.useForm();

  const [rootList, setRootList] = useState<ThemeNode[]>([]);
  const [rootTotal, setRootTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rootLoading, setRootLoading] = useState(false);

  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<ThemeNode | null>(null);

  const [saveVisible, setSaveVisible] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editingNode, setEditingNode] = useState<ThemeNode | null>(null);
  const [targetParent, setTargetParent] = useState<ThemeNode | null>(null);

  const loadRoots = (page?: { pageNum?: number; pageSize?: number }) => {
    const nextPageNum = page?.pageNum ?? pageNum;
    const nextPageSize = page?.pageSize ?? pageSize;
    const query = queryForm.getFieldsValue();
    const params: Record<string, any> = {
      sourceType: query.sourceType || undefined,
      keyword: query.keyword?.trim() || undefined,
      pageNum: nextPageNum,
      pageSize: nextPageSize
    };
    setRootLoading(true);
    get<ThemePage>(API_PREFIX, params)
      .then((res: any) => {
        setRootList(res.data?.list || []);
        setRootTotal(res.data?.total || 0);
      })
      .catch(() => undefined)
      .finally(() => setRootLoading(false));
  };

  useEffect(() => {
    loadRoots();
  }, [pageNum, pageSize]);

  const openNode = (nodeKey: string) => {
    setDetailVisible(true);
    setDetailLoading(true);
    get<ThemeNode>(`${API_PREFIX}/${nodeKey}`)
      .then((res: any) => setDetail(res.data || null))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  };

  const openCreate = (parent?: ThemeNode | null) => {
    setEditingNode(null);
    setTargetParent(parent || null);
    saveForm.resetFields();
    saveForm.setFieldsValue({
      sourceType: 'all',
      themeKind: 'theme',
      stocks: []
    });
    setSaveVisible(true);
  };

  const openEdit = (node: ThemeNode) => {
    get<ThemeNode>(`${API_PREFIX}/${node.id}`)
      .then((res: any) => {
        const fullNode: ThemeNode = res.data;
        setEditingNode(fullNode);
        setTargetParent(fullNode.parentId ? ({ id: fullNode.parentId } as ThemeNode) : null);
        saveForm.resetFields();
        saveForm.setFieldsValue({
          name: fullNode.name,
          sourceType: fullNode.sourceType || 'all',
          themeKind: fullNode.themeKind || 'theme',
          rank: fullNode.rank,
          sortNo: fullNode.sortNo,
          stocks: (fullNode.stocks || []).map(item => ({
            code: item.code,
            name: item.name,
            exchange: item.exchange,
            coreLogic: item.coreLogic
          }))
        });
        setSaveVisible(true);
      })
      .catch(() => undefined);
  };

  const closeSave = () => {
    setSaveVisible(false);
    setEditingNode(null);
    setTargetParent(null);
    saveForm.resetFields();
  };

  const submitSave = () => {
    saveForm
      .validate()
      .then(values => {
        const parent = editingNode?.parentId
          ? ({ id: editingNode.parentId } as ThemeNode)
          : targetParent;
        const payload = normalizePayload(values, parent);
        const stockCodes = (payload.stocks || []).map((item: any) => item.code).filter(Boolean);
        if (new Set(stockCodes).size !== stockCodes.length) {
          Message.error('成分股代码不能重复');
          return;
        }
        setSaveLoading(true);
        const request = editingNode
          ? put(`${API_PREFIX}/${editingNode.id}`, payload)
          : post(API_PREFIX, payload);
        request
          .then((res: any) => {
            Message.success(editingNode ? '题材节点修改成功' : '题材节点新增成功');
            const saved: ThemeNode = res.data;
            closeSave();
            loadRoots();
            if (detailVisible) {
              const refreshKey = editingNode?.id || targetParent?.id || saved?.id;
              if (refreshKey) openNode(refreshKey);
            }
          })
          .catch(() => undefined)
          .finally(() => setSaveLoading(false));
      })
      .catch(() => undefined);
  };

  const deleteNode = (node: ThemeNode) => {
    Modal.confirm({
      title: '确认删除题材节点',
      content: `确定删除「${node.name}」？该节点的全部子题材、节点成分股和对应AI内容都会一起删除。`,
      okButtonProps: { status: 'danger' },
      onOk: () =>
        del(`${API_PREFIX}/${node.id}`).then(() => {
          Message.success('删除成功');
          loadRoots();
          if (node.parentId) {
            openNode(node.parentId);
          } else {
            setDetailVisible(false);
            setDetail(null);
          }
        })
    });
  };

  const rootColumns: TableColumnProps[] = [
    {
      title: '根题材',
      dataIndex: 'name',
      width: 240,
      render: (_, record: ThemeNode) => (
        <Button type="text" className="node-name-button" onClick={() => openNode(record.id)}>
          {record.name}
        </Button>
      )
    },
    {
      title: '来源',
      dataIndex: 'sourceType',
      width: 100,
      render: value => <Tag color={value === 'curated' ? 'arcoblue' : 'gray'}>{sourceLabel(value)}</Tag>
    },
    { title: '类型', dataIndex: 'themeKind', width: 110, render: value => value || '-' },
    { title: '排名', dataIndex: 'rank', width: 80, render: value => value ?? '-' },
    { title: '直接子题材', dataIndex: 'childCount', width: 110, render: value => value ?? 0 },
    { title: '直接成分股', dataIndex: 'stockCount', width: 110, render: value => value ?? 0 },
    {
      title: '操作',
      width: 240,
      fixed: 'right',
      render: (_, record: ThemeNode) => (
        <Space>
          <Button size="small" onClick={() => openNode(record.id)}>管理</Button>
          <Button size="small" onClick={() => openEdit(record)}>编辑</Button>
          <Button size="small" status="danger" onClick={() => deleteNode(record)}>删除</Button>
        </Space>
      )
    }
  ];

  const childColumns: TableColumnProps[] = [
    {
      title: '子题材',
      dataIndex: 'name',
      width: 220,
      render: (_, record: ThemeNode) => (
        <Button type="text" className="node-name-button" onClick={() => openNode(record.id)}>
          {record.name}
        </Button>
      )
    },
    { title: '层级', dataIndex: 'level', width: 80 },
    { title: '排序', dataIndex: 'sortNo', width: 80 },
    { title: '子题材数', dataIndex: 'childCount', width: 100, render: value => value ?? 0 },
    { title: '成分股数', dataIndex: 'stockCount', width: 100, render: value => value ?? 0 },
    {
      title: '操作',
      width: 220,
      render: (_, record: ThemeNode) => (
        <Space>
          <Button size="small" onClick={() => openNode(record.id)}>进入</Button>
          <Button size="small" onClick={() => openEdit(record)}>编辑</Button>
          <Button size="small" status="danger" onClick={() => deleteNode(record)}>删除</Button>
        </Space>
      )
    }
  ];

  const stockColumns: TableColumnProps[] = [
    { title: '代码', dataIndex: 'code', width: 100 },
    { title: '名称', dataIndex: 'name', width: 130 },
    { title: '交易所', dataIndex: 'exchange', width: 90, render: value => value || '-' },
    { title: '核心逻辑', dataIndex: 'coreLogic', render: value => value || '-' }
  ];

  const editingIsRoot = editingNode ? !editingNode.parentId : !targetParent;

  return (
    <div className="agent-theme-management table-flex">
      <div className="page-heading">
        <div>
          <h2>题材树管理</h2>
          <p>维护根题材、递归子题材及节点直接成分股</p>
        </div>
        <Button type="primary" icon={<IconPlus />} onClick={() => openCreate(null)}>
          新增根题材
        </Button>
      </div>

      <Alert
        type="info"
        content="涨跌幅与K线统一根据成分股日行情实时计算，此处不再维护快照和手工涨跌幅。"
        className="schema-tip"
      />

      <div className="query-card">
        <Form form={queryForm} layout="inline" initialValues={{ sourceType: '' }}>
          <FormItem field="sourceType">
            <Select style={{ width: 190 }} options={SOURCE_OPTIONS} />
          </FormItem>
          <FormItem field="keyword">
            <Input allowClear placeholder="搜索根题材名称" style={{ width: 240 }} />
          </FormItem>
          <FormItem>
            <Space>
              <Button
                type="primary"
                onClick={() => {
                  setPageNum(1);
                  loadRoots({ pageNum: 1, pageSize });
                }}
              >
                查询
              </Button>
              <Button icon={<IconRefresh />} onClick={() => loadRoots()}>刷新</Button>
            </Space>
          </FormItem>
        </Form>
      </div>

      <Table
        rowKey="id"
        columns={rootColumns}
        data={rootList}
        loading={rootLoading}
        scroll={{ x: 1100 }}
        pagination={{
          current: pageNum,
          pageSize,
          total: rootTotal,
          showTotal: true,
          showJumper: true,
          sizeCanChange: true,
          onChange: (nextPage, nextSize) => {
            setPageNum(nextPage);
            setPageSize(nextSize);
          }
        }}
      />

      <Modal
        title="题材节点管理"
        visible={detailVisible}
        onCancel={() => {
          setDetailVisible(false);
          setDetail(null);
        }}
        footer={null}
        style={{ width: 1120 }}
        className="agent-theme-detail-modal"
        unmountOnExit
      >
        <Spin loading={detailLoading} style={{ width: '100%' }}>
          {detail && (
            <div className="node-detail">
              <div className="node-detail-head">
                <div>
                  <div className="node-path">{detail.path || detail.name}</div>
                  <h3>{detail.name}</h3>
                  <Space>
                    <Tag>{detail.type === 'ROOT' ? '根题材' : `第 ${detail.level} 层子题材`}</Tag>
                    {detail.sourceType && <Tag color="arcoblue">{sourceLabel(detail.sourceType)}</Tag>}
                    <span>直接子题材 {detail.childCount || 0}</span>
                    <span>直接成分股 {detail.stockCount || 0}</span>
                  </Space>
                </div>
                <Space>
                  {detail.parentId && (
                    <Button onClick={() => openNode(detail.parentId!)}>返回父节点</Button>
                  )}
                  <Button onClick={() => openEdit(detail)}>编辑当前节点</Button>
                  <Button type="primary" icon={<IconPlus />} onClick={() => openCreate(detail)}>
                    新增子题材
                  </Button>
                  <Button status="danger" onClick={() => deleteNode(detail)}>删除</Button>
                </Space>
              </div>

              <section className="detail-section">
                <div className="section-title">直接子题材</div>
                <Table
                  rowKey="id"
                  columns={childColumns}
                  data={detail.children || []}
                  pagination={false}
                  scroll={{ x: 900 }}
                  noDataElement="暂无直接子题材"
                />
              </section>

              <section className="detail-section">
                <div className="section-title">节点直接成分股</div>
                <Table
                  rowKey="code"
                  columns={stockColumns}
                  data={detail.stocks || []}
                  pagination={false}
                  scroll={{ x: 760 }}
                  noDataElement="暂无直接成分股"
                />
              </section>
            </div>
          )}
        </Spin>
      </Modal>

      <Modal
        title={editingNode ? '编辑题材节点' : targetParent ? '新增子题材' : '新增根题材'}
        visible={saveVisible}
        onCancel={closeSave}
        onOk={submitSave}
        confirmLoading={saveLoading}
        style={{ width: 960 }}
        className="agent-theme-save-modal"
        unmountOnExit
        maskClosable={false}
      >
        <Form form={saveForm} layout="vertical" autoComplete="off">
          {targetParent && !editingNode && (
            <Alert type="info" content={`父题材：${targetParent.path || targetParent.name}`} />
          )}
          <div className="save-grid">
            <FormItem
              label="题材名称"
              field="name"
              rules={[{ required: true, message: '请输入题材名称' }]}
            >
              <Input placeholder="题材节点名称" maxLength={255} />
            </FormItem>
            <FormItem label="同级排序" field="sortNo">
              <InputNumber min={0} placeholder="留空自动排在末尾" style={{ width: '100%' }} />
            </FormItem>
            {editingIsRoot && (
              <>
                <FormItem
                  label="榜单来源"
                  field="sourceType"
                  rules={[{ required: true, message: '请选择榜单来源' }]}
                >
                  <Select options={SOURCE_OPTIONS.filter(item => item.value)} />
                </FormItem>
                <FormItem label="题材类型" field="themeKind">
                  <Input placeholder="topic / theme" maxLength={32} />
                </FormItem>
                <FormItem label="榜单排名" field="rank">
                  <InputNumber min={1} placeholder="可留空" style={{ width: '100%' }} />
                </FormItem>
              </>
            )}
          </div>

          <div className="save-section-title">节点直接成分股</div>
          <Form.List field="stocks">
            {(fields, { add, remove }) => (
              <div className="save-list">
                {fields.map((item, index) => (
                  <div className="save-list-row" key={item.key}>
                    <FormItem
                      field={`${item.field}.code`}
                      rules={[{ required: true, message: '请输入代码' }]}
                      style={{ marginBottom: 0, width: 110 }}
                    >
                      <Input placeholder="股票代码" maxLength={20} />
                    </FormItem>
                    <FormItem
                      field={`${item.field}.name`}
                      style={{ marginBottom: 0, width: 130 }}
                    >
                      <Input placeholder="股票名称" maxLength={100} />
                    </FormItem>
                    <FormItem
                      field={`${item.field}.exchange`}
                      style={{ marginBottom: 0, width: 90 }}
                    >
                      <Input placeholder="SH/SZ" maxLength={16} />
                    </FormItem>
                    <FormItem
                      field={`${item.field}.coreLogic`}
                      style={{ marginBottom: 0, flex: 1 }}
                    >
                      <Input placeholder="该个股在当前题材下的核心逻辑" />
                    </FormItem>
                    <Button
                      type="text"
                      status="danger"
                      icon={<IconDelete />}
                      onClick={() => remove(index)}
                    />
                  </div>
                ))}
                <Button type="dashed" long icon={<IconPlus />} onClick={() => add()}>
                  添加成分股
                </Button>
              </div>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
}

export default AgentThemeManagement;
